import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { connect } from 'react-redux';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Paper from '@mui/material/Paper';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import TextField from '@mui/material/TextField';
import VisibilityIcon from '@mui/icons-material/Visibility';
import CloseIcon from '@mui/icons-material/Close';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import TopBar from '../TopBar';
import agent from '../../agent';
import ModerationPreviewDialog, { MODERATION_DIALOG_Z_INDEX } from './ModerationPreviewDialog';

const SECTIONS = [
  { kind: 'freak', key: 'freaks', label: 'Freaks' },
  { kind: 'freakHub', key: 'freakHubs', label: 'FreakHubs' },
  { kind: 'reel', key: 'reels', label: 'Reels' },
];

const formatDate = (iso) =>
  new Date(iso).toLocaleString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });

const itemTitle = (kind, item) =>
  kind === 'freak' ? item.title : kind === 'freakHub' ? item.name : item.caption || item.slug;

const itemLink = (kind, item) => {
  if (kind === 'freak') return `/freak/${item.slug}`;
  if (kind === 'freakHub') return `/freak-hub/${item.slug}`;
  return item.freak?.slug ? `/freak/${item.freak.slug}` : null;
};

const EMPTY = { freaks: [], freakHubs: [], reels: [] };

export const AdminModerationView = ({ currentUser }) => {
  const navigate = useNavigate();
  const [pending, setPending] = useState(EMPTY);
  const [loading, setLoading] = useState(true);
  const [busySlug, setBusySlug] = useState(null);
  const [error, setError] = useState('');
  const [previewing, setPreviewing] = useState(null); // { kind, item }
  const [rejecting, setRejecting] = useState(null); // { kind, item }
  const [reason, setReason] = useState('');

  useEffect(() => {
    const isAdmin = currentUser?.userRoles?.some(r => r.role === 'admin');
    if (!isAdmin) {
      navigate('/404');
      return;
    }

    agent.Moderation.getPending()
      .then(res => setPending({ ...EMPTY, ...res }))
      .catch(() => navigate('/404'))
      .finally(() => setLoading(false));
  }, []);

  const removeItem = (kind, slug) => {
    const { key } = SECTIONS.find(s => s.kind === kind);
    setPending(current => ({ ...current, [key]: current[key].filter(i => i.slug !== slug) }));
  };

  const runAction = async (kind, slug, action) => {
    setBusySlug(slug);
    setError('');
    try {
      await action();
      removeItem(kind, slug);
      return true;
    } catch (e) {
      setError(e?.response?.body?.errors?.message || 'Action failed, please try again.');
      return false;
    } finally {
      setBusySlug(null);
    }
  };

  // approving is only possible from the preview, so the admin always sees the full content first
  const approvePreviewed = async () => {
    const { kind, item } = previewing;
    if (await runAction(kind, item.slug, () => agent.Moderation.approve(kind, item.slug))) {
      setPreviewing(null);
    }
  };

  const openPreview = (kind, item) => {
    setError('');
    setPreviewing({ kind, item });
  };

  const rejectPreviewed = () => {
    setRejecting(previewing);
    setPreviewing(null);
  };

  const confirmReject = async () => {
    const { kind, item } = rejecting;
    setRejecting(null);
    await runAction(kind, item.slug, () => agent.Moderation.reject(kind, item.slug, reason));
    setReason('');
  };

  const total = SECTIONS.reduce((sum, { key }) => sum + pending[key].length, 0);

  const renderItem = (kind, item) => {
    const link = itemLink(kind, item);
    const busy = busySlug === item.slug;
    return (
      <Paper key={item.slug} sx={{ p: { xs: 1.5, sm: 2 }, mb: 1.5, borderRadius: 2, overflow: 'hidden' }}>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1, mb: 0.5 }}>
          <Typography sx={{ fontWeight: 700, overflowWrap: 'anywhere', flex: '1 1 200px', minWidth: 0 }}>
            {itemTitle(kind, item)}
          </Typography>
          {item.context && <Chip size="small" label={item.context} />}
        </Box>
        <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mb: 1, overflowWrap: 'anywhere' }}>
          @{item.author?.username} · {formatDate(item.createdAt)}
        </Typography>
        {item.description && (
          <Typography
            variant="body2"
            sx={{
              mb: 1,
              overflowWrap: 'anywhere',
              // the full text is in the preview; keep the list scannable on phones
              display: '-webkit-box',
              WebkitLineClamp: 4,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden',
            }}
          >
            {item.description}
          </Typography>
        )}
        {kind === 'reel' && item.videoUrl && (
          <Box
            component="video"
            src={item.videoUrl}
            poster={item.thumbnailUrl || undefined}
            controls
            playsInline
            preload="metadata"
            sx={{ width: '100%', maxWidth: 320, maxHeight: { xs: '55vh', sm: 480 }, borderRadius: 2, display: 'block', mb: 1, mx: { xs: 'auto', sm: 0 }, background: '#000' }}
          />
        )}
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
          <Button
            variant="contained"
            startIcon={<VisibilityIcon />}
            disabled={busy}
            onClick={() => openPreview(kind, item)}
            sx={{ minHeight: 44, textTransform: 'none', flex: { xs: '1 1 100%', sm: '0 0 auto' } }}
          >
            Preview &amp; approve
          </Button>
          <Button
            variant="outlined"
            color="error"
            startIcon={<CloseIcon />}
            disabled={busy}
            onClick={() => setRejecting({ kind, item })}
            sx={{ minHeight: 44, textTransform: 'none', flex: { xs: '1 1 0', sm: '0 0 auto' } }}
          >
            Reject
          </Button>
          {link && (
            <Button
              startIcon={<OpenInNewIcon />}
              onClick={() => window.open(link, '_blank', 'noopener')}
              sx={{ minHeight: 44, textTransform: 'none', flex: { xs: '1 1 0', sm: '0 0 auto' } }}
            >
              Open
            </Button>
          )}
          {busy && <CircularProgress size={24} sx={{ alignSelf: 'center' }} />}
        </Box>
      </Paper>
    );
  };

  return (
    <div>
      <TopBar />
      <Box sx={{ p: { xs: 1.5, sm: 3 }, pb: { xs: 12, sm: 3 }, maxWidth: 800, mx: 'auto' }}>
        <Typography variant="h5" sx={{ mb: 1, fontWeight: 700 }}>
          Moderation
        </Typography>
        <Typography variant="body2" sx={{ mb: 3, color: 'text.secondary' }}>
          Public posts stay hidden until approved here. Open a preview to review the full content before approving.
        </Typography>

        {error && (
          <Typography color="error" sx={{ mb: 2 }}>
            {error}
          </Typography>
        )}

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 8 }}>
            <CircularProgress />
          </Box>
        ) : total === 0 ? (
          <Typography sx={{ color: 'text.secondary', mt: 4, textAlign: 'center' }}>
            Nothing waiting for approval.
          </Typography>
        ) : (
          SECTIONS.filter(({ key }) => pending[key].length > 0).map(({ kind, key, label }) => (
            <Box key={kind} sx={{ mb: 3 }}>
              <Typography variant="h6" sx={{ mb: 1, fontWeight: 600 }}>
                {label} ({pending[key].length})
              </Typography>
              {pending[key].map(item => renderItem(kind, item))}
            </Box>
          ))
        )}
      </Box>

      <ModerationPreviewDialog
        preview={previewing}
        title={previewing && itemTitle(previewing.kind, previewing.item)}
        busy={!!previewing && busySlug === previewing.item.slug}
        error={error}
        onClose={() => setPreviewing(null)}
        onApprove={approvePreviewed}
        onReject={rejectPreviewed}
      />

      <Dialog
        open={!!rejecting}
        onClose={() => setRejecting(null)}
        fullWidth
        maxWidth="sm"
        sx={{ zIndex: MODERATION_DIALOG_Z_INDEX }}
        PaperProps={{ sx: { mx: { xs: 1.5, sm: 4 }, width: { xs: 'calc(100% - 24px)', sm: undefined } } }}
      >
        <DialogTitle sx={{ overflowWrap: 'anywhere' }}>Reject “{rejecting && itemTitle(rejecting.kind, rejecting.item)}”</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            fullWidth
            multiline
            minRows={2}
            margin="dense"
            label="Reason (sent to the author)"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRejecting(null)} sx={{ minHeight: 44 }}>Cancel</Button>
          <Button color="error" variant="contained" onClick={confirmReject} sx={{ minHeight: 44 }}>
            Reject
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

const mapStateToProps = state => ({
  currentUser: state.common.currentUser,
});

export default connect(mapStateToProps)(AdminModerationView);
